import { zodResolver } from '@hookform/resolvers/zod'
import { format, parseISO } from 'date-fns'
import {
  AlertCircle,
  Eye,
  EyeOff,
  FileText,
  GraduationCap,
  Medal,
  Plus,
  Trash2,
  Trophy,
  UserCircle2,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'

import { FilePondDocumentUpload } from '@/components/file-pond-upload'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { FileViewerDialog } from '@/components/common/FileViewerDialog'
import { useUpdateApplication } from '@/utils/api/membership-service'
import type { ApplicationUpdateDTO } from '@/utils/api/membership-service/models/application-update-dt-o'
import { ApplicationUpdateDTOGender } from '@/utils/api/membership-service/models/application-update-dto-gender'
import type { ApplicationResponseDTO } from '@/utils/api/membership-service/models/application-response-dt-o'
import type { EducationCreateDTO } from '@/utils/api/membership-service/models/education-create-dt-o'
import type { EducationResponseDTO } from '@/utils/api/membership-service/models/education-response-dt-o'
import type { EmploymentHistoryCreateDTO } from '@/utils/api/membership-service/models/employment-history-create-dt-o'
import type { EmploymentHistoryResponseDTO } from '@/utils/api/membership-service/models/employment-history-response-dt-o'
import type { ProfessionalAffiliationCreateDTO } from '@/utils/api/membership-service/models/professional-affiliation-create-dt-o'
import type { AwardCreateDTO } from '@/utils/api/membership-service/models/award-create-dt-o'
import type { SupportingDocumentCreateDTO } from '@/utils/api/membership-service/models/supporting-document-create-dt-o'
import { useChangePassword } from '@/utils/api/user-service'

import { ApplicationStatusBadge } from '@/features/admin/applications/components/ApplicationStatusBadge'
import { useLatestApplication } from '@/features/membership/hooks/useLatestApplication'
import { ApplicationResponseDTOStatus as ApplicationStatus } from '@/utils/api/membership-service/models'

const employmentEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  organisation: z.string().optional(),
  jobTitle: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  isCurrent: z.boolean().optional(),
  reportingTo: z.string().optional(),
  keyResponsibilities: z.string().optional(),
  isPersisted: z.boolean().optional(),
})

const educationEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  institution: z.string().optional(),
  qualification: z.string().optional(),
  fieldOfStudy: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  gradeGpa: z.string().optional(),
  notes: z.string().optional(),
  isPersisted: z.boolean().optional(),
})

const documentEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  documentType: z.string().optional(),
  description: z.string().optional(),
  fileId: z.string().optional(),
  fileName: z.string().optional(),
  fileUrl: z.string().optional(),
  isPersisted: z.boolean().optional(),
})

const affiliationEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  organizationName: z.string().optional(),
  membershipLevel: z.string().optional(),
  membershipNumber: z.string().optional(),
  joinDate: z.string().optional(),
  expiryDate: z.string().optional(),
  isActive: z.boolean().optional(),
  isPersisted: z.boolean().optional(),
})

const awardEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  awardName: z.string().optional(),
  issuingOrganisation: z.string().optional(),
  dateReceived: z.string().optional(),
  description: z.string().optional(),
  isPersisted: z.boolean().optional(),
})

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  middleName: z.string().optional(),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Enter a valid email'),
  phoneNumbers: z.string().min(1, 'Phone number is required'),
  categoryName: z.string().min(1),
  dateOfBirth: z.string().optional(),
  gender: z.enum([ApplicationUpdateDTOGender.MALE, ApplicationUpdateDTOGender.FEMALE]).optional(),
  placeOfResident: z.string().optional(),
  organisation: z.string().optional(),
  studentId: z.string().optional(),
  institution: z.string().optional(),
  graduationYear: z.string().optional(),
  yearsOfExperience: z.string().optional(),
  professionalLicenseNumber: z.string().optional(),
  employmentHistory: z.array(employmentEntrySchema),
  education: z.array(educationEntrySchema),
  documents: z.array(documentEntrySchema),
  affiliations: z.array(affiliationEntrySchema),
  awards: z.array(awardEntrySchema),
})

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  path: ['confirmPassword'],
  message: "Passwords don't match",
})

type EmploymentEntryFormValue = z.infer<typeof employmentEntrySchema>
type EducationEntryFormValue = z.infer<typeof educationEntrySchema>
type DocumentEntryFormValue = z.infer<typeof documentEntrySchema>
type AffiliationEntryFormValue = z.infer<typeof affiliationEntrySchema>
type AwardEntryFormValue = z.infer<typeof awardEntrySchema>
type ProfileFormValues = z.infer<typeof profileSchema>
type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>
type ProfileSectionKey =
  | 'summary'
  | 'personal'
  | 'contact'
  | 'education'
  | 'employment'
  | 'affiliations'
  | 'awards'
  | 'documents'

function generateTemporaryId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch (_error) { }
  return Math.random().toString(36).slice(2, 12)
}

function createEmptyProfileFormValues(): ProfileFormValues {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    emailAddress: '',
    phoneNumbers: '',
    categoryName: '',
    dateOfBirth: '',
    gender: undefined,
    placeOfResident: '',
    organisation: '',
    studentId: '',
    institution: '',
    graduationYear: '',
    yearsOfExperience: '',
    professionalLicenseNumber: '',
    employmentHistory: [],
    education: [],
    documents: [],
    affiliations: [],
    awards: [],
  }
}

function createEmptyEmploymentEntry(): EmploymentEntryFormValue {
  return {
    id: generateTemporaryId(),
    organisation: '',
    jobTitle: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
    reportingTo: '',
    keyResponsibilities: '',
    isPersisted: false,
  }
}

function createEmptyEducationEntry(): EducationEntryFormValue {
  return {
    id: generateTemporaryId(),
    institution: '',
    qualification: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    gradeGpa: '',
    notes: '',
    isPersisted: false,
  }
}

function createEmptyDocumentEntry(): DocumentEntryFormValue {
  return {
    id: generateTemporaryId(),
    documentType: '',
    description: '',
    fileId: '',
    fileName: '',
    fileUrl: '',
    isPersisted: false,
  }
}

function createEmptyAffiliationEntry(): AffiliationEntryFormValue {
  return {
    id: generateTemporaryId(),
    organizationName: '',
    membershipLevel: '',
    membershipNumber: '',
    joinDate: '',
    expiryDate: '',
    isActive: false,
    isPersisted: false,
  }
}

function createEmptyAwardEntry(): AwardEntryFormValue {
  return {
    id: generateTemporaryId(),
    awardName: '',
    issuingOrganisation: '',
    dateReceived: '',
    description: '',
    isPersisted: false,
  }
}

function normaliseDateInput(value?: string | null): string {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.includes('T')) {
    const [datePart] = trimmed.split('T')
    return datePart ?? ''
  }
  return trimmed
}

function normaliseNumberInput(value?: string | null): number | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = Number.parseInt(trimmed, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

function formatMembershipDate(value?: string | null, pattern = 'PPP'): string | null {
  if (!value) return null
  try {
    return format(parseISO(value), pattern)
  } catch {
    return null
  }
}

function mapEmploymentHistoryToForm(entries: EmploymentHistoryResponseDTO[] | undefined): EmploymentEntryFormValue[] {
  if (!entries?.length) {
    return []
  }

  return entries.map((entry) => ({
    id: entry.id ?? generateTemporaryId(),
    organisation: entry.organisation ?? '',
    jobTitle: entry.jobTitle ?? '',
    startDate: normaliseDateInput(entry.startDate),
    endDate: normaliseDateInput(entry.endDate),
    isCurrent: entry.isCurrent ?? false,
    reportingTo: entry.reportingTo ?? '',
    keyResponsibilities: entry.keyResponsibilities ?? '',
    isPersisted: typeof entry.id === 'number',
  }))
}

function mapEducationToForm(entries: EducationResponseDTO[] | undefined): EducationEntryFormValue[] {
  if (!entries?.length) {
    return []
  }

  return entries.map((entry) => ({
    id: entry.id ?? generateTemporaryId(),
    institution: entry.institution ?? '',
    qualification: entry.qualification ?? '',
    fieldOfStudy: entry.fieldOfStudy ?? '',
    startDate: normaliseDateInput(entry.startDate),
    endDate: normaliseDateInput(entry.endDate),
    gradeGpa: entry.gradeGpa ?? '',
    notes: entry.notes ?? '',
    isPersisted: typeof entry.id === 'number',
  }))
}

function mapDocumentsToForm(entries: SupportingDocumentCreateDTO[] | undefined): DocumentEntryFormValue[] {
  if (!entries?.length) {
    return []
  }

  return entries.map((entry) => ({
    id: entry.fileId ?? generateTemporaryId(),
    documentType: entry.documentType ?? '',
    description: entry.description ?? '',
    fileId: entry.fileId ?? '',
    fileName: entry.fileName ?? '',
    fileUrl: entry.fileUrl ?? '',
    isPersisted: Boolean(entry.fileId),
  }))
}

function mapAffiliationsToForm(
  entries: ProfessionalAffiliationCreateDTO[] | undefined,
): AffiliationEntryFormValue[] {
  if (!entries?.length) {
    return []
  }

  return entries.map((entry) => ({
    id: generateTemporaryId(),
    organizationName: entry.organizationName ?? '',
    membershipLevel: entry.membershipLevel ?? '',
    membershipNumber: entry.membershipNumber ?? '',
    joinDate: normaliseDateInput(entry.joinDate),
    expiryDate: normaliseDateInput(entry.expiryDate),
    isActive: entry.isActive ?? false,
    isPersisted: true,
  }))
}

function mapAwardsToForm(entries: AwardCreateDTO[] | undefined): AwardEntryFormValue[] {
  if (!entries?.length) {
    return []
  }

  return entries.map((entry) => ({
    id: generateTemporaryId(),
    awardName: entry.awardName ?? '',
    issuingOrganisation: entry.issuingOrganisation ?? '',
    dateReceived: normaliseDateInput(entry.dateReceived),
    description: entry.description ?? '',
    isPersisted: true,
  }))
}

function mapApplicationToForm(application: ApplicationResponseDTO): ProfileFormValues {
  return {
    firstName: application.firstName ?? '',
    middleName: application.middleName ?? '',
    lastName: application.lastName ?? '',
    emailAddress: application.emailAddress ?? '',
    phoneNumbers: application.phoneNumbers ?? '',
    categoryName: application.categoryName ?? '',
    dateOfBirth: normaliseDateInput(application.dateOfBirth),
    gender: application.gender ?? undefined,
    placeOfResident: application.placeOfResident ?? '',
    organisation: application.organisation ?? '',
    studentId: application.studentId ?? '',
    institution: application.institution ?? '',
    graduationYear: application.graduationYear?.toString() ?? '',
    yearsOfExperience: application.yearsOfExperience?.toString() ?? '',
    professionalLicenseNumber: application.professionalLicenseNumber ?? '',
    employmentHistory: mapEmploymentHistoryToForm(application.employmentHistory),
    education: mapEducationToForm(application.education),
    documents: mapDocumentsToForm(application.documents),
    affiliations: mapAffiliationsToForm(application.affiliations),
    awards: mapAwardsToForm(application.awards),
  }
}

function resolveApplicationStatus(status?: string | null): ApplicationStatus {
  switch (status) {
    case ApplicationStatus.APPROVED:
    case ApplicationStatus.PENDING:
    case ApplicationStatus.REJECTED:
      return status
    default:
      return ApplicationStatus.PENDING
  }
}

function buildUpdatePayload(values: ProfileFormValues): ApplicationUpdateDTO {
  const employmentHistory = values.employmentHistory.reduce<EmploymentHistoryCreateDTO[]>((acc, entry) => {
    const { isPersisted: _isPersisted, ...data } = entry
    const organisation = data.organisation?.trim() ?? ''
    const jobTitle = data.jobTitle?.trim() ?? ''
    if (!organisation || !jobTitle) {
      return acc
    }

    const payload: EmploymentHistoryCreateDTO = {
      companyName: organisation,
      position: jobTitle,
    }

    if (entry.startDate?.trim()) {
      payload.startDate = data.startDate?.trim()
    }
    if (!data.isCurrent && data.endDate?.trim()) {
      payload.endDate = data.endDate.trim()
    }
    if (data.isCurrent) {
      payload.isCurrent = true
    }

    const responsibilities: string[] = []
    if (data.keyResponsibilities?.trim()) {
      responsibilities.push(data.keyResponsibilities.trim())
    }
    if (data.reportingTo?.trim()) {
      responsibilities.push(`Reports to: ${data.reportingTo.trim()}`)
    }
    if (responsibilities.length > 0) {
      payload.responsibilities = responsibilities.join('\n')
    }

    acc.push(payload)
    return acc
  }, [])

  const education = values.education.reduce<EducationCreateDTO[]>((acc, entry) => {
    const { isPersisted: _isPersisted, ...data } = entry
    const institution = data.institution?.trim() ?? ''
    const qualification = data.qualification?.trim() ?? ''
    if (!institution || !qualification) {
      return acc
    }

    const payload: EducationCreateDTO = {
      institutionName: institution,
      qualification,
    }

    if (data.fieldOfStudy?.trim()) {
      payload.fieldOfStudy = data.fieldOfStudy.trim()
    }
    if (data.startDate?.trim()) {
      payload.startDate = data.startDate.trim()
    }
    if (data.endDate?.trim()) {
      payload.endDate = data.endDate.trim()
    }
    if (data.gradeGpa?.trim()) {
      payload.gradeGpa = data.gradeGpa.trim()
    }
    if (data.notes?.trim()) {
      payload.notes = data.notes.trim()
    }

    acc.push(payload)
    return acc
  }, [])

  const documents = values.documents.reduce<SupportingDocumentCreateDTO[]>((acc, entry) => {
    const { isPersisted: _isPersisted, ...data } = entry
    const fileId = data.fileId?.trim() ?? ''
    const fileUrl = data.fileUrl?.trim() ?? ''
    const fileName = data.fileName?.trim() ?? ''
    const documentType = data.documentType?.trim() ?? ''

    if (!fileId || !fileUrl || !fileName || !documentType) {
      return acc
    }

    const payload: SupportingDocumentCreateDTO = {
      fileId,
      fileUrl,
      fileName,
      documentType,
    }

    if (entry.description?.trim()) {
      payload.description = entry.description.trim()
    }

    acc.push(payload)
    return acc
  }, [])

  const affiliations = values.affiliations.reduce<ProfessionalAffiliationCreateDTO[]>((acc, entry) => {
    const { isPersisted: _isPersisted, ...data } = entry
    const organisation = data.organizationName?.trim() ?? ''
    if (!organisation) {
      return acc
    }

    const payload: ProfessionalAffiliationCreateDTO = {
      organizationName: organisation,
    }

    if (data.membershipLevel?.trim()) {
      payload.membershipLevel = data.membershipLevel.trim()
    }
    if (data.membershipNumber?.trim()) {
      payload.membershipNumber = data.membershipNumber.trim()
    }
    if (data.joinDate?.trim()) {
      payload.joinDate = data.joinDate.trim()
    }
    if (data.expiryDate?.trim()) {
      payload.expiryDate = data.expiryDate.trim()
    }
    if (data.isActive) {
      payload.isActive = true
    }

    acc.push(payload)
    return acc
  }, [])

  const awards = values.awards.reduce<AwardCreateDTO[]>((acc, entry) => {
    const { isPersisted: _isPersisted, ...data } = entry
    const awardName = data.awardName?.trim() ?? ''
    if (!awardName) {
      return acc
    }

    const payload: AwardCreateDTO = {
      awardName,
    }

    if (data.issuingOrganisation?.trim()) {
      payload.issuingOrganisation = data.issuingOrganisation.trim()
    }
    if (data.dateReceived?.trim()) {
      payload.dateReceived = data.dateReceived.trim()
    }
    if (data.description?.trim()) {
      payload.description = data.description.trim()
    }

    acc.push(payload)
    return acc
  }, [])

  return {
    categoryName: values.categoryName,
    firstName: values.firstName.trim(),
    middleName: values.middleName?.trim() ? values.middleName.trim() : undefined,
    lastName: values.lastName.trim(),
    emailAddress: values.emailAddress.trim(),
    phoneNumbers: values.phoneNumbers.trim(),
    dateOfBirth: values.dateOfBirth?.trim() ? values.dateOfBirth : undefined,
    gender: values.gender,
    placeOfResident: values.placeOfResident?.trim() ? values.placeOfResident : undefined,
    organisation: values.organisation?.trim() ? values.organisation : undefined,
    studentId: values.studentId?.trim() ? values.studentId : undefined,
    institution: values.institution?.trim() ? values.institution : undefined,
    graduationYear: normaliseNumberInput(values.graduationYear),
    yearsOfExperience: normaliseNumberInput(values.yearsOfExperience),
    professionalLicenseNumber: values.professionalLicenseNumber?.trim()
      ? values.professionalLicenseNumber
      : undefined,
    employmentHistory: employmentHistory.length > 0 ? employmentHistory : undefined,
    education: education.length > 0 ? education : undefined,
    documents: documents.length > 0 ? documents : undefined,
    affiliations: affiliations.length > 0 ? affiliations : undefined,
    awards: awards.length > 0 ? awards : undefined,
  }
}

interface UserProfilePageProps {
  applicationId?: number
}

export function UserProfilePage({ applicationId }: UserProfilePageProps = {}) {
  const { latestApplication, applications, query: applicationsQuery } = useLatestApplication()
  const updateApplicationMutation = useUpdateApplication()
  const changePasswordMutation = useChangePassword()
  const summarySectionRef = useRef<HTMLDivElement>(null)
  const personalSectionRef = useRef<HTMLDivElement>(null)
  const contactSectionRef = useRef<HTMLDivElement>(null)
  const educationSectionRef = useRef<HTMLDivElement>(null)
  const employmentSectionRef = useRef<HTMLDivElement>(null)
  const affiliationsSectionRef = useRef<HTMLDivElement>(null)
  const awardsSectionRef = useRef<HTMLDivElement>(null)
  const documentsSectionRef = useRef<HTMLDivElement>(null)
  const [activeProfileSection, setActiveProfileSection] = useState<ProfileSectionKey>('summary')

  const profileSectionRefs: Record<ProfileSectionKey, typeof summarySectionRef> = {
    summary: summarySectionRef,
    personal: personalSectionRef,
    contact: contactSectionRef,
    education: educationSectionRef,
    employment: employmentSectionRef,
    affiliations: affiliationsSectionRef,
    awards: awardsSectionRef,
    documents: documentsSectionRef,
  }

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: createEmptyProfileFormValues(),
  })

  const changePasswordForm = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation,
  } = useFieldArray({
    control: form.control,
    name: 'education',
  })

  const {
    fields: employmentFields,
    append: appendEmployment,
    remove: removeEmployment,
  } = useFieldArray({
    control: form.control,
    name: 'employmentHistory',
  })

  const {
    fields: documentFields,
    append: appendDocument,
    remove: removeDocument,
  } = useFieldArray({
    control: form.control,
    name: 'documents',
  })

  const {
    fields: affiliationFields,
    append: appendAffiliation,
    remove: removeAffiliation,
  } = useFieldArray({
    control: form.control,
    name: 'affiliations',
  })

  const {
    fields: awardFields,
    append: appendAward,
    remove: removeAward,
  } = useFieldArray({
    control: form.control,
    name: 'awards',
  })

  const selectedApplication = useMemo(() => {
    if (typeof applicationId === 'number') {
      return applications.find((application) => application.id === applicationId) ?? null
    }
    return latestApplication
  }, [applicationId, applications, latestApplication])

  useEffect(() => {
    if (selectedApplication) {
      form.reset(mapApplicationToForm(selectedApplication))
    }
  }, [form, selectedApplication])

  const isInitialLoading = applicationsQuery.isLoading && !applicationsQuery.data
  const isLookingUpApplication = typeof applicationId === 'number' && applicationsQuery.isLoading
  const requestedApplicationMissing
    = typeof applicationId === 'number' && !isLookingUpApplication && !selectedApplication
  const isSaving = updateApplicationMutation.isPending

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!selectedApplication || !selectedApplication.id) return

    const payload = buildUpdatePayload(values)

    try {
      await updateApplicationMutation.mutateAsync({
        id: selectedApplication.id,
        data: payload,
      })
      toast.success('Profile updated', {
        description: 'Your membership application details have been saved.',
      })
      await applicationsQuery.refetch()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'An unexpected error occurred.'
      toast.error('Unable to update profile', {
        description: message,
      })
    }
  })

  const handleChangePasswordSubmit = changePasswordForm.handleSubmit(async (values) => {
    try {
      const response = await changePasswordMutation.mutateAsync({
        data: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
      })
      changePasswordForm.reset()
      toast.success('Password updated', {
        description: response?.data?.message || 'Your password has been updated successfully.',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update password.'
      const responseMessage = (error as any)?.response?.data?.message
      toast.error('Unable to update password', {
        description: responseMessage || message,
      })
    }
  })

  const isChangingPassword = changePasswordMutation.isPending

  const [activeFileIndex, setActiveFileIndex] = useState<number | null>(null)
  const confirmRemoval = useCallback((label: string) => {
    if (typeof window === 'undefined') {
      return true
    }
    return window.confirm(`Remove this ${label}? This action cannot be undone.`)
  }, [])

  if (isInitialLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-5 w-56" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
          <CardFooter>
            <Skeleton className="h-10 w-32" />
          </CardFooter>
        </Card>
      </div>
    )
  }

  const educationEntries = form.watch('education')
  const employmentEntries = form.watch('employmentHistory')
  const documentEntries = form.watch('documents')
  const affiliationEntries = form.watch('affiliations')
  const awardEntries = form.watch('awards')

  if (!selectedApplication) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex flex-col items-start gap-2">
            <UserCircle2 className="h-10 w-10 text-muted-foreground" />
            <CardTitle>{requestedApplicationMissing ? 'Application not found' : 'No application found'}</CardTitle>
            <CardDescription>
              {requestedApplicationMissing
                ? 'The application you selected could not be found. Open your applications list and choose a valid record.'
                : 'We couldn&apos;t find any membership application linked to your account. Submit a membership application to manage your profile details here.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Next steps</AlertTitle>
              <AlertDescription>
                {requestedApplicationMissing
                  ? 'Go back to your applications page and reopen the application you need to update.'
                  : 'Start a new membership application to record your professional details. Once submitted, you can manage them from this profile page.'}
              </AlertDescription>
            </Alert>
            <div className="mt-4">
              <Button asChild>
                <Link to={requestedApplicationMissing ? '/membership/applications' : '/membership/apply'}>
                  {requestedApplicationMissing ? 'Back to my applications' : 'Start membership application'}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {typeof selectedApplication.id === 'number'
            ? `Edit membership application #${selectedApplication.id}`
            : 'My membership profile'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Review and update the information submitted for your membership application.
        </p>
      </div>

      {selectedApplication.rejectionReason ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Changes requested by reviewer</AlertTitle>
          <AlertDescription>{selectedApplication.rejectionReason}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs
                value={activeProfileSection}
                onValueChange={(value) => {
                  const nextValue = value as ProfileSectionKey
                  setActiveProfileSection(nextValue)
                  profileSectionRefs[nextValue]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className="space-y-4"
              >
                <TabsList className="flex-wrap h-auto gap-2">
                  <TabsTrigger value="summary" className="flex-none">Summary</TabsTrigger>
                  <TabsTrigger value="personal" className="flex-none">Personal</TabsTrigger>
                  <TabsTrigger value="contact" className="flex-none">Contact</TabsTrigger>
                  <TabsTrigger value="education" className="flex-none">Education</TabsTrigger>
                  <TabsTrigger value="employment" className="flex-none">Employment</TabsTrigger>
                  <TabsTrigger value="affiliations" className="flex-none">Affiliations</TabsTrigger>
                  <TabsTrigger value="awards" className="flex-none">Awards</TabsTrigger>
                  <TabsTrigger value="documents" className="flex-none">Documents</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="space-y-6">
                <section ref={summarySectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle>Membership summary</CardTitle>
                      <CardDescription>
                        Snapshot of this membership application and its current review status.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Status</p>
                          <div className="mt-1">
                            <ApplicationStatusBadge status={resolveApplicationStatus(selectedApplication.status)} />
                          </div>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Membership period</p>
                          <p className="mt-1 text-sm font-medium">
                            {formatMembershipDate(selectedApplication.membershipStartAt) ?? '—'} –{' '}
                            {formatMembershipDate(selectedApplication.membershipExpiryAt) ?? '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Renewals</p>
                          <p className="mt-1 text-sm font-medium">
                            {typeof selectedApplication.renewalCount === 'number'
                              ? `${selectedApplication.renewalCount} time${selectedApplication.renewalCount === 1 ? '' : 's'}`
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>

                <section ref={personalSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle>Personal information</CardTitle>
                      <CardDescription>
                        Update your core profile information as it appears on your membership records.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First name</FormLabel>
                              <FormControl>
                                <Input placeholder="First name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="middleName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Middle name</FormLabel>
                              <FormControl>
                                <Input placeholder="Middle name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last name</FormLabel>
                              <FormControl>
                                <Input placeholder="Last name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Gender</FormLabel>
                              <Select
                                value={field.value ?? ''}
                                onValueChange={(value) => field.onChange(value || undefined)}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select gender" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value={ApplicationUpdateDTOGender.MALE}>Male</SelectItem>
                                  <SelectItem value={ApplicationUpdateDTOGender.FEMALE}>Female</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="dateOfBirth"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Date of birth</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="categoryName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Membership category</FormLabel>
                              <FormControl>
                                <Input {...field} disabled />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </section>

                <section ref={contactSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle>Contact details</CardTitle>
                      <CardDescription>
                        Keep your contact information up to date so we can reach you about membership updates.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="emailAddress"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email address</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="you@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phoneNumbers"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone number</FormLabel>
                              <FormControl>
                                <Input placeholder="+256..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="placeOfResident"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Place of residence</FormLabel>
                            <FormControl>
                              <Input placeholder="City or district" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                </section>

                <section ref={educationSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <GraduationCap className="h-4 w-4 text-primary" />
                        Education
                      </CardTitle>
                      <CardDescription>
                        Maintain your academic background and supporting qualifications.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="studentId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Student ID</FormLabel>
                              <FormControl>
                                <Input placeholder="Student ID" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="institution"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Institution</FormLabel>
                              <FormControl>
                                <Input placeholder="University or institution" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="graduationYear"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Graduation year</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. 2026" inputMode="numeric" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium">Education history</p>
                            <p className="text-xs text-muted-foreground">
                              Add each qualification or academic programme you want on record.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => appendEducation(createEmptyEducationEntry())}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add qualification
                          </Button>
                        </div>

                        {educationFields.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No education entries yet. Use &quot;Add qualification&quot; to capture your academic background.
                          </p>
                        ) : (
                          <div className="space-y-4">
                            {educationFields.map((field, index) => {
                              const entry = educationEntries?.[index]
                              return (
                                <div key={field.id} className="space-y-4 rounded-lg border p-4">
                                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="grid flex-1 gap-4 sm:grid-cols-2">
                                      <FormField
                                        control={form.control}
                                        name={`education.${index}.institution`}
                                        render={({ field: controllerField }) => (
                                          <FormItem>
                                            <FormLabel>Institution</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Institution name" {...controllerField} />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <FormField
                                        control={form.control}
                                        name={`education.${index}.qualification`}
                                        render={({ field: controllerField }) => (
                                          <FormItem>
                                            <FormLabel>Qualification</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Degree, diploma, certificate..." {...controllerField} />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => {
                                        if (!confirmRemoval('education entry')) return
                                        removeEducation(index)
                                      }}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Remove
                                    </Button>
                                  </div>

                                  <div className="grid gap-4 sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`education.${index}.fieldOfStudy`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Field of study (optional)</FormLabel>
                                          <FormControl>
                                            <Input placeholder="Field of study" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`education.${index}.gradeGpa`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Grade / GPA (optional)</FormLabel>
                                          <FormControl>
                                            <Input placeholder="Grade or GPA" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  <div className="grid gap-4 sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`education.${index}.startDate`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Start date (optional)</FormLabel>
                                          <FormControl>
                                            <Input type="date" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`education.${index}.endDate`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>End date (optional)</FormLabel>
                                          <FormControl>
                                            <Input type="date" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  <FormField
                                    control={form.control}
                                    name={`education.${index}.notes`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormLabel>Notes (optional)</FormLabel>
                                        <FormControl>
                                          <Textarea
                                            placeholder="Additional details, honours, or achievements"
                                            {...controllerField}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />

                                  {entry?.id ? (
                                    <p className="text-xs text-muted-foreground">
                                      Reference ID: {entry.id}
                                    </p>
                                  ) : null}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </section>

                <section ref={employmentSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Medal className="h-4 w-4 text-primary" />
                        Employment history
                      </CardTitle>
                      <CardDescription>
                        Document your professional experience to keep your membership records current.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="organisation"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Current organisation</FormLabel>
                              <FormControl>
                                <Input placeholder="Organisation" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="yearsOfExperience"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Years of experience</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. 5" inputMode="numeric" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="professionalLicenseNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Professional licence number</FormLabel>
                              <FormControl>
                                <Input placeholder="Licence number" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-medium">Employment history</p>
                            <p className="text-xs text-muted-foreground">
                              Add each role you&apos;d like the institute to keep on record.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => appendEmployment(createEmptyEmploymentEntry())}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add role
                          </Button>
                        </div>

                        {employmentFields.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No employment history added yet. Use &quot;Add role&quot; to capture your experience.
                          </p>
                        ) : (
                          <div className="space-y-4">
                            {employmentFields.map((field, index) => {
                              const isCurrent = employmentEntries?.[index]?.isCurrent ?? false
                              const entryId = employmentEntries?.[index]?.id
                              return (
                                <div key={field.id} className="space-y-4 rounded-lg border p-4">
                                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="grid flex-1 gap-4 sm:grid-cols-2">
                                      <FormField
                                        control={form.control}
                                        name={`employmentHistory.${index}.organisation`}
                                        render={({ field: controllerField }) => (
                                          <FormItem>
                                            <FormLabel>Organisation</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Organisation name" {...controllerField} />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <FormField
                                        control={form.control}
                                        name={`employmentHistory.${index}.jobTitle`}
                                        render={({ field: controllerField }) => (
                                          <FormItem>
                                            <FormLabel>Job title</FormLabel>
                                            <FormControl>
                                              <Input placeholder="Role or position" {...controllerField} />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => {
                                        if (!confirmRemoval('employment entry')) return
                                        removeEmployment(index)
                                      }}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Remove
                                    </Button>

                                  </div>

                                  <div className="grid gap-4 sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`employmentHistory.${index}.startDate`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Start date</FormLabel>
                                          <FormControl>
                                            <Input type="date" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`employmentHistory.${index}.endDate`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>End date</FormLabel>
                                          <FormControl>
                                            <Input type="date" disabled={Boolean(isCurrent)} {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  <FormField
                                    control={form.control}
                                    name={`employmentHistory.${index}.isCurrent`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormControl>
                                          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-3">
                                            <Checkbox
                                              checked={Boolean(controllerField.value)}
                                              onCheckedChange={(checked) => controllerField.onChange(checked === true)}
                                              id={`employment-current-${field.id}`}
                                            />
                                            <FormLabel
                                              htmlFor={`employment-current-${field.id}`}
                                              className="text-sm font-medium"
                                            >
                                              I currently work in this role
                                            </FormLabel>
                                          </div>
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />

                                  <div className="grid gap-4 sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`employmentHistory.${index}.reportingTo`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Reporting to (optional)</FormLabel>
                                          <FormControl>
                                            <Input placeholder="Line manager" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`employmentHistory.${index}.keyResponsibilities`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Key responsibilities (optional)</FormLabel>
                                          <FormControl>
                                            <Textarea
                                              placeholder="Summarise responsibilities and achievements"
                                              {...controllerField}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  {entryId ? (
                                    <p className="text-xs text-muted-foreground">Reference ID: {entryId}</p>
                                  ) : null}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </section>

                <section ref={affiliationsSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Users className="h-4 w-4 text-primary" />
                        Professional affiliations
                      </CardTitle>
                      <CardDescription>
                        Show the professional bodies or networks you belong to.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium">Affiliation list</p>
                          <p className="text-xs text-muted-foreground">
                            Include organisations that recognise your membership or certification.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => appendAffiliation(createEmptyAffiliationEntry())}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add affiliation
                        </Button>
                      </div>

                      {affiliationFields.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No affiliations recorded. Use &quot;Add affiliation&quot; to keep your memberships on file.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {affiliationFields.map((field, index) => {
                            const entry = affiliationEntries?.[index]
                            const checkboxId = `affiliation-active-${field.id}`
                            return (
                              <div key={field.id} className="space-y-4 rounded-lg border p-4">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                  <div className="grid flex-1 gap-4 sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name={`affiliations.${index}.organizationName`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Organisation</FormLabel>
                                          <FormControl>
                                            <Input placeholder="Professional body" {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`affiliations.${index}.membershipLevel`}
                                      render={({ field: controllerField }) => (
                                        <FormItem>
                                          <FormLabel>Membership level (optional)</FormLabel>
                                          <FormControl>
                                            <Input placeholder="Fellow, associate, member..." {...controllerField} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => {
                                      if (!confirmRemoval('affiliation')) return
                                      removeAffiliation(index)
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove
                                  </Button>

                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <FormField
                                    control={form.control}
                                    name={`affiliations.${index}.membershipNumber`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormLabel>Membership number (optional)</FormLabel>
                                        <FormControl>
                                          <Input placeholder="Membership / registration number" {...controllerField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`affiliations.${index}.joinDate`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormLabel>Join date (optional)</FormLabel>
                                        <FormControl>
                                          <Input type="date" {...controllerField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <FormField
                                    control={form.control}
                                    name={`affiliations.${index}.expiryDate`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormLabel>Expiry date (optional)</FormLabel>
                                        <FormControl>
                                          <Input type="date" {...controllerField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`affiliations.${index}.isActive`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormControl>
                                          <div className="flex h-full items-center gap-2 rounded-md border border-border bg-muted/40 p-3">
                                            <Checkbox
                                              checked={Boolean(controllerField.value)}
                                              onCheckedChange={(checked) => controllerField.onChange(checked === true)}
                                              id={checkboxId}
                                            />
                                            <FormLabel htmlFor={checkboxId} className="text-sm font-medium">
                                              Membership currently active
                                            </FormLabel>
                                          </div>
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                {entry?.membershipNumber ? (
                                  <p className="text-xs text-muted-foreground">
                                    Membership number: {entry.membershipNumber}
                                  </p>
                                ) : null}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </section>

                <section ref={awardsSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Trophy className="h-4 w-4 text-primary" />
                        Awards &amp; recognition
                      </CardTitle>
                      <CardDescription>
                        Highlight achievements that strengthen your membership profile.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium">Awards list</p>
                          <p className="text-xs text-muted-foreground">
                            Mention notable awards, honours, or recognitions you&apos;ve received.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => appendAward(createEmptyAwardEntry())}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add award
                        </Button>
                      </div>

                      {awardFields.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No awards captured yet. Use &quot;Add award&quot; to record achievements.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {awardFields.map((field, index) => {
                            const entry = awardEntries?.[index]
                            return (
                              <div key={field.id} className="space-y-4 rounded-lg border p-4">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                  <FormField
                                    control={form.control}
                                    name={`awards.${index}.awardName`}
                                    render={({ field: controllerField }) => (
                                      <FormItem className="flex-1">
                                        <FormLabel>Award name</FormLabel>
                                        <FormControl>
                                          <Input placeholder="Award or recognition title" {...controllerField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => {
                                      if (!confirmRemoval('award')) return
                                      removeAward(index)
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove
                                  </Button>

                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                  <FormField
                                    control={form.control}
                                    name={`awards.${index}.issuingOrganisation`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormLabel>Issuing organisation (optional)</FormLabel>
                                        <FormControl>
                                          <Input placeholder="Organisation or body" {...controllerField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`awards.${index}.dateReceived`}
                                    render={({ field: controllerField }) => (
                                      <FormItem>
                                        <FormLabel>Date received (optional)</FormLabel>
                                        <FormControl>
                                          <Input type="date" {...controllerField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                <FormField
                                  control={form.control}
                                  name={`awards.${index}.description`}
                                  render={({ field: controllerField }) => (
                                    <FormItem>
                                      <FormLabel>Description (optional)</FormLabel>
                                      <FormControl>
                                        <Textarea placeholder="Reason for the recognition or additional notes" {...controllerField} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                {entry?.id ? (
                                  <p className="text-xs text-muted-foreground">Reference ID: {entry.id}</p>
                                ) : null}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </section>

                <section ref={documentsSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText className="h-4 w-4 text-primary" />
                        Supporting documents
                      </CardTitle>
                      <CardDescription>
                        Upload and manage the documents required for your membership category.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium">Uploaded files</p>
                          <p className="text-xs text-muted-foreground">
                            Add documents that verify your qualifications and professional background.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => appendDocument(createEmptyDocumentEntry())}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add document
                        </Button>
                      </div>

                      {documentFields.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No supporting documents on file. Use &quot;Add document&quot; to upload files that support your membership.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {documentFields.map((field, index) => {
                            const entry = documentEntries?.[index]
                            const hasFile = Boolean(entry?.fileId && entry?.fileUrl)

                            return (
                              <div key={field.id} className="space-y-4 rounded-lg border p-4">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                  <FormField
                                    control={form.control}
                                    name={`documents.${index}.documentType`}
                                    render={({ field: controllerField }) => (
                                      <FormItem className="flex-1">
                                        <FormLabel>Document name</FormLabel>
                                        <FormControl>
                                          <Input placeholder="e.g. Curriculum Vitae" {...controllerField} disabled={(selectedApplication.status === ApplicationStatus.APPROVED || selectedApplication.status === ApplicationStatus.RENEWED)} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => {
                                      if (!confirmRemoval('supporting document')) return
                                      removeDocument(index)
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove
                                  </Button>

                                </div>

                                <FormField
                                  control={form.control}
                                  name={`documents.${index}.description`}
                                  render={({ field: controllerField }) => (
                                    <FormItem>
                                      <FormLabel>Description (optional)</FormLabel>
                                      <FormControl>
                                        <Textarea placeholder="Notes about this document" {...controllerField} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="text-sm">
                                    {hasFile ? (
                                      <div className="flex flex-col gap-1">
                                        <span className="font-medium">{entry?.fileName}</span>
                                        <span className="text-xs text-muted-foreground break-words">
                                          {entry?.fileUrl}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground">No file attached yet.</span>
                                    )}
                                  </div>
                                  {hasFile ? (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setActiveFileIndex(index)}
                                    >
                                      View document
                                    </Button>
                                  ) : null}
                                </div>

                                <FilePondDocumentUpload
                                  className="mt-2"
                                  onUploadComplete={(fileId, fileUrl, filename) => {
                                    form.setValue(`documents.${index}.fileId`, fileId, {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    })
                                    form.setValue(`documents.${index}.fileUrl`, fileUrl, {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    })
                                    form.setValue(`documents.${index}.fileName`, filename, {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    })
                                    toast.success('Document uploaded', {
                                      description: `${filename} is attached to your profile.`,
                                    })
                                  }}
                                  onRemove={() => {
                                    if (!confirmRemoval('uploaded file')) return
                                    form.setValue(`documents.${index}.fileId`, '', {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    })
                                    form.setValue(`documents.${index}.fileUrl`, '', {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    })
                                    form.setValue(`documents.${index}.fileName`, '', {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                    })
                                  }}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </section>

              </div>

              <CardFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (selectedApplication) {
                      form.reset(mapApplicationToForm(selectedApplication))
                    } else {
                      form.reset(createEmptyProfileFormValues())
                    }
                  }}
                  disabled={isSaving}
                >
                  Reset changes
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Change password</CardTitle>
              <CardDescription>
                Update your account password to keep your membership profile secure.
              </CardDescription>
            </CardHeader>
            <Form {...changePasswordForm}>
              <form onSubmit={handleChangePasswordSubmit}>
                <CardContent className="space-y-4">
                  <FormField
                    control={changePasswordForm.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showCurrentPassword ? 'text' : 'password'}
                              autoComplete="current-password"
                              disabled={isChangingPassword}
                              className="pr-10"
                              {...field}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-pressed={showCurrentPassword}
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                              onClick={() => setShowCurrentPassword((prev) => !prev)}
                            >
                              {showCurrentPassword
                                ? <EyeOff className="h-4 w-4 text-muted-foreground" />
                                : <Eye className="h-4 w-4 text-muted-foreground" />}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={changePasswordForm.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showNewPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                disabled={isChangingPassword}
                                className="pr-10"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-pressed={showNewPassword}
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowNewPassword((prev) => !prev)}
                              >
                                {showNewPassword
                                  ? <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  : <Eye className="h-4 w-4 text-muted-foreground" />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={changePasswordForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm new password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                disabled={isChangingPassword}
                                className="pr-10"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-pressed={showConfirmPassword}
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                              >
                                {showConfirmPassword
                                  ? <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  : <Eye className="h-4 w-4 text-muted-foreground" />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button type="submit" disabled={isChangingPassword}>
                    {isChangingPassword ? 'Updating…' : 'Update password'}
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        </TabsContent>
      </Tabs>

      <FileViewerDialog
        open={activeFileIndex !== null}
        onClose={() => setActiveFileIndex(null)}
        fileUrl={
          activeFileIndex !== null ? documentEntries?.[activeFileIndex]?.fileUrl ?? undefined : undefined
        }
        fileName={
          activeFileIndex !== null ? documentEntries?.[activeFileIndex]?.fileName ?? undefined : undefined
        }
        description={
          activeFileIndex !== null
            ? documentEntries?.[activeFileIndex]?.description ?? undefined
            : undefined
        }
      />
    </div>
  )
}
